// ---------------------------------------------------------------------------
// The credit line baked into every rendered map image — "© 25 Maps |
// © Mapbox | © OpenStreetMap" — small and grey, centred near the bottom.
//
// Every render (order-sheet preview, AR texture, Stripe/email upload, the
// 60cm print, both map styles) goes through renderMapImage.ts /
// admin/print-render.js with Mapbox's own `attributionControl` UI turned
// off (it's a corner logo + "Improve this map" link, wrong for a print) —
// this line is what actually carries the required Mapbox/OSM credit,
// alongside the brand's own.
//
// Unlike the Suburbs searched-label (a Mapbox GL layer at a geo point) or
// the Streets location caption (locationBadge.ts), this always draws,
// regardless of style — so it has to be independent of both.
// ---------------------------------------------------------------------------

// Exact CSS spec (at the 1024px reference frame), from the design source:
//   padding: 2.167px; gap: 1.355px; border-radius: 2.167px;
//   background: #FFF; color: #242424; font: 100 3.251px Roboto;
// `gap` isn't applicable — the chip has no other flex children to space
// from the single text run.
export const MAP_ATTRIBUTION_STYLE = {
  text: '© 25 Maps | © Mapbox | © OpenStreetMap',
  font: 'Roboto',
  fontWeight: 100,
  fontSizePx: 3.251,
  color: '#242424',
  boxColor: '#ffffff',
  boxPaddingXPx: 2.167,
  boxPaddingYPx: 2.167,
  boxCornerRadiusPx: 2.167,
  // Gap between the frame's bottom edge and the chip's own bottom edge.
  marginBottomPx: 32,
};

async function ensureFontLoaded(spec: string): Promise<void> {
  if (typeof document === 'undefined' || !('fonts' in document)) return;
  try {
    await document.fonts.load(spec);
  } catch {
    // Best-effort — canvas falls back to the next font in the stack.
  }
}

/** Traces a rounded-rect as a single path (not four arcs + separate fill
 *  calls) so fill has no seam at large scale. Mirrors locationBadge.ts's
 *  own copy — kept small enough that duplicating it beats a shared-utility
 *  import across these independently-mirrored (TS + print-render.js)
 *  rendering modules. */
function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/** Draws the attribution line (white chip, muted text) onto a size×size
 *  canvas 2D context, centred horizontally near the bottom. Returns the Y
 *  coordinate of the top of the chip it drew, so a caller stacking
 *  something above it (the Streets location caption) knows where its own
 *  bottom margin should start. */
export async function drawMapAttribution(
  ctx: CanvasRenderingContext2D,
  size: number,
): Promise<{ topY: number }> {
  const cfg = MAP_ATTRIBUTION_STYLE;
  const scale = size / 1024;
  const fontSize = cfg.fontSizePx * scale;
  await ensureFontLoaded(`${cfg.fontWeight} ${fontSize}px ${cfg.font}`);

  ctx.save();
  ctx.font = `${cfg.fontWeight} ${fontSize}px ${cfg.font}, Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  const metrics = ctx.measureText(cfg.text);
  const ascent = metrics.actualBoundingBoxAscent || fontSize * 0.75;
  const descent = metrics.actualBoundingBoxDescent || fontSize * 0.2;
  const textWidth = metrics.width;

  const paddingX = cfg.boxPaddingXPx * scale;
  const paddingY = cfg.boxPaddingYPx * scale;
  const cornerRadius = cfg.boxCornerRadiusPx * scale;
  const marginBottom = cfg.marginBottomPx * scale;

  const boxBottomY = size - marginBottom;
  const boxHeight = ascent + descent + paddingY * 2;
  const boxTopY = boxBottomY - boxHeight;
  const boxWidth = textWidth + paddingX * 2;
  const boxLeftX = size / 2 - boxWidth / 2;
  const baselineY = boxBottomY - paddingY - descent;

  ctx.fillStyle = cfg.boxColor;
  roundedRectPath(ctx, boxLeftX, boxTopY, boxWidth, boxHeight, cornerRadius);
  ctx.fill();

  ctx.fillStyle = cfg.color;
  ctx.fillText(cfg.text, size / 2, baselineY);
  ctx.restore();

  return { topY: boxTopY };
}
